import { AxiosError } from 'axios'
import { useCallback } from 'react'

import { useQuery, UseQueryOptions } from '@tanstack/react-query'

import { CashOperationItemInterface } from '@/interfaces/ServerInterfaces'

import APIServices from '../../api/APIServices'

export interface CashOperationQueryParams {
  per_page?: number | null
  page?: number | null
  economicCycleId?: number | null
  codeCurrency?: string | null
  operation?: string | null
  category?: string | null
  areaIds?: string | null
  dateFrom?: string | null
  dateTo?: string | null
  amountFrom?: string | null
  amountTo?: string | null
  madeById?: number | null
}

// Create a unique, descriptive key for this query
const CASH_OPERATION_QUERY_KEY = 'cash-operations'

/**
 * Response type for cash operation query
 */
export interface CashOperationQueryResponse {
  totalItems: number
  currentPage: number
  totalPages: number
  items: CashOperationItemInterface[]
}

/**
 * Fetch cash operations from the API
 */
const fetchCashOperations = async (
  params?: CashOperationQueryParams
): Promise<CashOperationQueryResponse> => {
  // Filter out null/undefined values to clean up the request
  const cleanParams = Object.fromEntries(
    Object.entries(params || {}).filter(([_, value]) => value != null)
  )

  const response = await APIServices.get('/cashoperation', {
    params: cleanParams,
  })
  return response.data
}

/**
 * Custom hook for fetching cash operations with filtering
 *
 * @param params - Query parameters for filtering cash operations
 * @param options - TanStack Query options for customizing query behavior
 * @returns Query result object containing data, loading states, and utility functions
 */
export const useCashOperationQuery = ({
  params,
  options,
  businessId,
}: {
  params?: CashOperationQueryParams
  options?: Partial<UseQueryOptions<CashOperationQueryResponse, AxiosError>>
  businessId: number
}) => {
  // Normalize params with defaults
  const normalizedParams: CashOperationQueryParams = {
    page: 1,
    per_page: 10,
    ...params,
  }

  // Query hook - now properly reactive to external parameter changes
  const queryResult = useQuery<CashOperationQueryResponse, AxiosError>({
    queryKey: [CASH_OPERATION_QUERY_KEY, normalizedParams, businessId],
    queryFn: () => fetchCashOperations(normalizedParams),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })

  // Helper functions for pagination metadata
  const getCurrentPage = useCallback(() => {
    return normalizedParams.page || 1
  }, [normalizedParams.page])

  const getPageSize = useCallback(() => {
    return normalizedParams.per_page || 10
  }, [normalizedParams.per_page])

  const hasNextPage = useCallback(() => {
    if (!queryResult.data) return false
    const { currentPage, totalPages } = queryResult.data
    return currentPage < totalPages
  }, [queryResult.data])

  const hasPreviousPage = useCallback(() => {
    const currentPage = getCurrentPage()
    return currentPage > 1
  }, [getCurrentPage])

  return {
    ...queryResult,
    pagination: {
      currentPage: getCurrentPage(),
      pageSize: getPageSize(),
      hasNextPage: hasNextPage(),
      hasPreviousPage: hasPreviousPage(),
      totalPages: queryResult.data?.totalPages || 0,
      totalItems: queryResult.data?.totalItems || 0,
      params: normalizedParams,
    },
  }
}

/**
 * Helper function to get the cash operation query key
 * Useful for cache invalidation or prefetching
 */
export const getCashOperationQueryKey = () => [CASH_OPERATION_QUERY_KEY]
